/**
 * Investigation form component (Module 24).
 * Provides form for creating and editing investigations.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { TextField } from '@core/components/mui';
import { investigationService } from '../../services/investigationService';
import { agentService } from '../../services/agentService';
import { useNotificationStore } from '../../store/notificationStore';
import { ROUTES } from '../../utils/routes';
import type { AgentDefinitionDto } from '../../types/agent';
import {
  IncidentSeverities,
  InvestigationStatuses,
  type IncidentSeverity,
  type InvestigationStatus,
  type CreateInvestigationRequest,
  type UpdateInvestigationRequest,
  type InvestigationTemplateDto,
} from '../../types/investigation';

/**
 * Investigation form component for creating and editing investigations.
 */
export const InvestigationForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { showNotification } = useNotificationStore();

  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [templates, setTemplates] = useState<InvestigationTemplateDto[]>([]);

  const [loading, setLoading] = useState(isEdit);
  const [loadingLookups, setLoadingLookups] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [agentDefinitionId, setAgentDefinitionId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>(IncidentSeverities.Medium);
  const [incidentNumber, setIncidentNumber] = useState('');
  const [incidentOccurredAt, setIncidentOccurredAt] = useState('');
  const [templateId, setTemplateId] = useState<string>('');

  // Findings (editable mostly on edit, but safe on create too)
  const [investigationStatus, setInvestigationStatus] = useState<InvestigationStatus>(InvestigationStatuses.NotStarted);
  const [rootCauseSummary, setRootCauseSummary] = useState('');
  const [remediationSteps, setRemediationSteps] = useState('');
  const [preventionRecommendations, setPreventionRecommendations] = useState('');

  const severityOptions = useMemo(
    () => [
      { value: IncidentSeverities.Low, label: 'Low' },
      { value: IncidentSeverities.Medium, label: 'Medium' },
      { value: IncidentSeverities.High, label: 'High' },
      { value: IncidentSeverities.Critical, label: 'Critical' },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: InvestigationStatuses.NotStarted, label: 'Not Started' },
      { value: InvestigationStatuses.InProgress, label: 'In Progress' },
      { value: InvestigationStatuses.Paused, label: 'Paused' },
      { value: InvestigationStatuses.Completed, label: 'Completed' },
      { value: InvestigationStatuses.Cancelled, label: 'Cancelled' },
    ],
    []
  );

  useEffect(() => {
    loadLookups();
    if (isEdit && id) {
      loadInvestigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadLookups = async () => {
    setLoadingLookups(true);
    try {
      const [agentsResult, templatesResult] = await Promise.all([
        agentService.getAgents({ pageSize: 1000, isActive: true }),
        investigationService.getInvestigationTemplates(true),
      ]);
      setAgents(agentsResult.items);
      setTemplates(templatesResult);
    } catch (err: any) {
      showNotification(err.message || 'Failed to load lookup data', 'error');
    } finally {
      setLoadingLookups(false);
    }
  };

  const loadInvestigation = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const inv = await investigationService.getInvestigation(id);
      setAgentDefinitionId(inv.agentDefinitionId);
      setTitle(inv.title);
      setDescription(inv.description || '');
      setSeverity(inv.severity);
      setIncidentNumber(inv.incidentNumber || '');
      setIncidentOccurredAt(inv.incidentOccurredAt ? inv.incidentOccurredAt.slice(0, 16) : '');
      setInvestigationStatus(inv.investigationStatus);
      setRootCauseSummary(inv.rootCauseSummary || '');
      setRemediationSteps(inv.remediationSteps || '');
      setPreventionRecommendations(inv.preventionRecommendations || '');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to load investigation';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!agentDefinitionId) {
      setError('Agent is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        const request: UpdateInvestigationRequest = {
          title: title.trim(),
          description: description.trim() || undefined,
          severity,
          investigationStatus,
          rootCauseSummary: rootCauseSummary.trim() || undefined,
          remediationSteps: remediationSteps.trim() || undefined,
          preventionRecommendations: preventionRecommendations.trim() || undefined,
        };
        await investigationService.updateInvestigation(id, request);
        showNotification('Investigation updated successfully', 'success');
      } else {
        const request: CreateInvestigationRequest = {
          agentDefinitionId,
          title: title.trim(),
          description: description.trim() || undefined,
          severity,
          incidentNumber: incidentNumber.trim() || undefined,
          incidentOccurredAt: incidentOccurredAt ? new Date(incidentOccurredAt).toISOString() : undefined,
          templateId: templateId || undefined,
        };
        await investigationService.createInvestigation(request);
        showNotification('Investigation created successfully', 'success');
      }

      navigate(ROUTES.INVESTIGATIONS);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save investigation';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || loadingLookups) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate(ROUTES.INVESTIGATIONS)} sx={{ mr: -1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
              {isEdit ? 'Edit Investigation' : 'Create Investigation'}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" fontWeight={600}>
                  Core Details
                </Typography>
                <Divider sx={{ mt: 2 }} />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Agent</InputLabel>
                  <Select
                    label="Agent"
                    value={agentDefinitionId}
                    onChange={(e) => setAgentDefinitionId(String(e.target.value))}
                    disabled={isEdit}
                  >
                    {agents.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Severity</InputLabel>
                  <Select
                    label="Severity"
                    value={severity}
                    onChange={(e) => setSeverity(Number(e.target.value) as IncidentSeverity)}
                  >
                    {severityOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  minRows={3}
                />
              </Grid>

              {!isEdit && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Incident Number (optional)"
                      value={incidentNumber}
                      onChange={(e) => setIncidentNumber(e.target.value)}
                      placeholder="INC-12345"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Incident Occurred At (optional)"
                      type="datetime-local"
                      value={incidentOccurredAt}
                      onChange={(e) => setIncidentOccurredAt(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <InputLabel>Template (optional)</InputLabel>
                      <Select
                        label="Template (optional)"
                        value={templateId}
                        onChange={(e) => setTemplateId(String(e.target.value))}
                      >
                        <MenuItem value="">None</MenuItem>
                        {templates.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>
                  Findings
                </Typography>
                <Divider sx={{ mt: 2 }} />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={investigationStatus}
                    onChange={(e) => setInvestigationStatus(Number(e.target.value) as InvestigationStatus)}
                  >
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Root Cause Summary"
                  value={rootCauseSummary}
                  onChange={(e) => setRootCauseSummary(e.target.value)}
                  multiline
                  minRows={3}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Remediation Steps"
                  value={remediationSteps}
                  onChange={(e) => setRemediationSteps(e.target.value)}
                  multiline
                  minRows={3}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Prevention Recommendations"
                  value={preventionRecommendations}
                  onChange={(e) => setPreventionRecommendations(e.target.value)}
                  multiline
                  minRows={3}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button variant="outlined" onClick={() => navigate(ROUTES.INVESTIGATIONS)}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

